import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle, XCircle, Eye, EyeOff, AlertCircle, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import logoImage from "@assets/openbox_1762897846924.png";

interface TokenValidation {
  valid: boolean;
  tokenType?: 'RESET' | 'SETUP' | 'ADMIN_SETUP';
  email?: string;
  firstName?: string;
  error?: string;
}

export default function SetPasswordPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<TokenValidation | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenInfo({ valid: false, error: 'No token provided' });
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(`/api/auth/validate-token/${token}`);
        const data = await response.json();
        setTokenInfo(data);
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenInfo({ valid: false, error: 'Failed to validate token' });
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
  
  const passwordRequirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'At least one number', met: /[0-9]/.test(password) },
  ];

  const allRequirementsMet = PASSWORD_REGEX.test(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      toast({
        title: 'Password Requirements',
        description: 'Please ensure your password meets all requirements.',
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords Don\'t Match',
        description: 'Please ensure both passwords are the same.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/set-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to set password',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Set password error:', error);
      toast({
        title: 'Error',
        description: 'An error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPageTitle = () => {
    if (!tokenInfo?.tokenType) return 'Set Your Password';
    switch (tokenInfo.tokenType) {
      case 'RESET':
        return 'Reset Your Password';
      case 'SETUP':
        return 'Complete Your Account Setup';
      case 'ADMIN_SETUP':
        return 'Welcome! Set Up Your Account';
      default:
        return 'Set Your Password';
    }
  };

  const getPageDescription = () => {
    if (!tokenInfo?.tokenType) return 'Create a secure password for your account.';
    const name = tokenInfo.firstName ? `Hi ${tokenInfo.firstName}! ` : '';
    switch (tokenInfo.tokenType) {
      case 'RESET':
        return `${name}Enter your new password below.`;
      case 'SETUP':
        return `${name}Your account has been approved! Create a password to get started.`;
      case 'ADMIN_SETUP':
        return `${name}An account has been created for you. Set up your password to get started.`;
      default:
        return 'Create a secure password for your account.';
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-3 sm:px-4 py-6 sm:py-12">
        <Card className="w-full max-w-md" data-testid="card-validating">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Validating your link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenInfo?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-3 sm:px-4 py-6 sm:py-12">
        <Card className="w-full max-w-md" data-testid="card-invalid-token">
          <CardHeader className="text-center p-4 sm:p-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="rounded-full bg-red-100 p-3">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-xl sm:text-2xl">Invalid or Expired Link</CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-2">
              {tokenInfo?.error || 'This password reset link is no longer valid.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Password reset links expire after 1 hour. Account setup links expire after 24 hours.
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <Link href="/forgot-password">
                <Button className="w-full" data-testid="button-request-new-link">
                  Request New Reset Link
                </Button>
              </Link>
              
              <Link href="/login">
                <Button variant="outline" className="w-full" data-testid="button-back-to-login">
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-3 sm:px-4 py-6 sm:py-12">
        <Card className="w-full max-w-md" data-testid="card-password-set-success">
          <CardHeader className="text-center p-4 sm:p-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-xl sm:text-2xl">Password Set Successfully!</CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-2">
              Your password has been {tokenInfo.tokenType === 'RESET' ? 'reset' : 'created'}. You can now log in with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <Link href="/login">
              <Button className="w-full" data-testid="button-go-to-login">
                Continue to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-3 sm:px-4 py-6 sm:py-12">
      <Card className="w-full max-w-md" data-testid="card-set-password">
        <CardHeader className="text-center p-4 sm:p-6">
          <div className="flex justify-center mb-3 sm:mb-4">
            <img 
              src={logoImage} 
              alt="OpenBox by ShopJimmy" 
              className="h-12 sm:h-16 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-xl sm:text-2xl">{getPageTitle()}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            {getPageDescription()}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {tokenInfo.email && (
            <div className="bg-muted rounded-lg p-3 mb-4">
              <p className="text-sm text-center">
                Setting password for: <strong>{tokenInfo.email}</strong>
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="input-password"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="button-toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Password Requirements:</p>
              {passwordRequirements.map((req, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  {req.met ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <X className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className={req.met ? 'text-green-600' : 'text-muted-foreground'}>
                    {req.text}
                  </span>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="input-confirm-password"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="button-toggle-confirm-password-visibility"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && (
                <div className="flex items-center gap-2 text-sm">
                  {passwordsMatch ? (
                    <>
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="text-green-600">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 text-red-500" />
                      <span className="text-red-500">Passwords don't match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full h-11" 
              disabled={isLoading || !allRequirementsMet || !passwordsMatch}
              data-testid="button-set-password"
            >
              <Lock className="h-4 w-4 mr-2" />
              {isLoading ? 'Setting Password...' : 'Set Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
